import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { grey, deepPurple, orange } from '@mui/material/colors';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { Science, SmartToy } from '@mui/icons-material';
import { SmsDirection, SmsActivityStatus } from '@trnsact/trnsact-shared-types';
import { ActivityCardProps, SmsConversationData } from '../../types';
import { DealSmsActivity } from '../../api/types';
import { CardContainer } from './common/CardContainer';
import { generatePersonaFullName, getPersonaInitials } from 'utils/formatters';
import { MessageBubble } from './MessageBubble';

interface Props extends ActivityCardProps<DealSmsActivity> {
  onOpenConversation?: (data: SmsConversationData) => void;
}

export const MessageCard = ({ entity, onOpenConversation }: Props) => {
  const classes = useStyles();

  const isUnread = entity.smsDirection === SmsDirection.Incoming && entity.status !== SmsActivityStatus.Read;
  const isDemoMode = entity.demoMode;

  const { ownerPgCreditApplication } = entity;
  const contactName = generatePersonaFullName(ownerPgCreditApplication) || (entity.isAgent ? 'AI Agent' : 'Unknown');
  const initials = getPersonaInitials(
    ownerPgCreditApplication?.firstName ?? undefined,
    ownerPgCreditApplication?.lastName ?? undefined
  );

  const handleClick = () => {
    onOpenConversation?.({
      voId: entity.parentId,
      conversationSequenceNumber: entity.conversationSequenceNumber,
      contactName,
      contactId: entity.ownerPgCreditApplicationId ?? null,
      contactPhone: ownerPgCreditApplication?.phone ?? null,
      contactTitle: ownerPgCreditApplication?.title ?? null,
      contactInitials: initials,
      isAgent: entity.isAgent,
      demoMode: isDemoMode,
    });
  };

  return (
    <CardContainer>
      <Box onClick={handleClick} sx={{ cursor: 'pointer' }}>
        {isUnread && <Box className={classes.unreadIndicator} />}
        {isDemoMode && <Box className={classes.demoIndicator} />}
        <Stack gap="0.75rem">
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" gap="0.75rem" alignItems="center">
              <Avatar className={classes.avatar}>
                <Typography fontWeight={500}>{initials}</Typography>
              </Avatar>

              <Stack>
                <Stack direction="row" gap="0.5rem" alignItems="center">
                  <Typography variant="subtitle1" fontWeight="bold">
                    {contactName}
                  </Typography>

                  {entity.isAgent && (
                    <Box className={classes.aiAgentChip}>
                      <SmartToy sx={{ fontSize: 12 }} />
                      <Typography align="center" component="span" variant="subtitle2" sx={{ fontSize: '12px' }}>
                        AI Agent
                      </Typography>
                    </Box>
                  )}

                  {isDemoMode && (
                    <Box className={classes.demoChip}>
                      <Science sx={{ fontSize: 12 }} />
                      <Typography align="center" component="span" variant="subtitle2" sx={{ fontSize: '12px' }}>
                        Demo
                      </Typography>
                    </Box>
                  )}

                  {ownerPgCreditApplication?.pc && (
                    <Box className={classes.primaryChip}>
                      <Typography
                        align="center"
                        component="span"
                        variant="subtitle2"
                        color="textSecondary"
                        sx={{ fontSize: '12px' }}
                      >
                        Primary
                      </Typography>
                    </Box>
                  )}
                </Stack>

                <Typography variant="caption" color="textSecondary">
                  {ownerPgCreditApplication?.phone}
                  {ownerPgCreditApplication?.title && ` • ${ownerPgCreditApplication.title}`}
                </Typography>
              </Stack>
            </Stack>

            {/*           {!!unreadCount && unreadCount > 0 && (
            <Badge
              badgeContent={`+${unreadCount}`}
              color="error"
              sx={{ '& .MuiBadge-badge': { position: 'static', transform: 'none' } }}
            />
          )} */}
          </Stack>

          <MessageBubble message={entity} />
        </Stack>
      </Box>
    </CardContainer>
  );
};

const useStyles = makeStyles({
  unreadIndicator: {
    width: '4px',
    borderRadius: 2,
    backgroundColor: '#2196f3',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
  },
  demoIndicator: {
    width: '4px',
    borderRadius: 2,
    backgroundColor: orange[400],
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
  },
  avatar: {
    width: 40,
    height: 40,
    backgroundColor: '#90caf9',
    color: 'black',
    fontSize: '14px',
  },
  primaryChip: {
    borderRadius: '6px',
    padding: '2px 6px',
    color: grey[700],
    backgroundColor: grey[200],
  },
  aiAgentChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    borderRadius: '6px',
    padding: '2px 6px',
    color: deepPurple[700],
    backgroundColor: deepPurple[50],
  },
  demoChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    borderRadius: '6px',
    padding: '2px 6px',
    color: orange[800],
    backgroundColor: orange[50],
  },
});
